import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { getStats } from "@/lib/api";
import { isOwnerDevice, setOwnerDevice } from "@/lib/analytics";
import { Eye, Users, Search, FileCheck } from "lucide-react";

// Admin -> Visitors: who opened flourma.in and who looked at their nutrition label.
const RESULT_TEXT = {
  found: "Label shown",
  unlocked: "Label shown (with last name)",
  needs_last_name: "Asked for last name",
  not_found: "Number not found",
  wrong_last_name: "Wrong last name",
};

const Card = ({ icon: Icon, label, value, note }) => (
  <div className="bg-white rounded-xl border border-[#D7CCC8] p-5">
    <div className="flex items-center gap-2 text-[#795548] text-sm"><Icon className="w-4 h-4" />{label}</div>
    <div className="text-3xl font-bold text-[#5D4037] mt-2">{value}</div>
    {note && <div className="text-xs text-[#A1887F] mt-1">{note}</div>}
  </div>
);

const VisitorStats = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [mine, setMine] = useState(isOwnerDevice());

  useEffect(() => {
    setError("");
    getStats(days).then(setData).catch(() => setError("Could not load visitor numbers. Please try again."));
  }, [days]);

  const t = data?.totals;
  const maxDay = Math.max(1, ...(data?.per_day || []).map((d) => d.visits + d.lookups));

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-[#5D4037]">Visitors</h1>
            <p className="text-[#795548]">Who opened flourma.in and who checked their nutrition label. No names or IP addresses are stored.</p>
          </div>
          <select value={days} onChange={(e) => setDays(+e.target.value)} className="border border-[#D7CCC8] rounded-md px-3 py-2 text-[#5D4037] bg-white" data-testid="stats-days">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 12 months</option>
          </select>
        </div>

        <div className="bg-white rounded-xl border border-[#D7CCC8] p-4 flex flex-wrap items-center justify-between gap-3" data-testid="owner-device">
          <div className="text-[#5D4037]">
            <b>{mine ? "This device is not counted" : "This device is counted"}</b>
            <div className="text-sm text-[#795548]">Your own visits and lookups are left out on devices marked as yours. To mark your phone, open <b>flourma.in/?me=1</b> on it once.</div>
          </div>
          <button
            onClick={() => { setOwnerDevice(!mine); setMine(isOwnerDevice()); }}
            className="rounded-md px-4 py-2 border-2 border-[#5D4037] text-[#5D4037] hover:bg-[#5D4037] hover:text-white transition-colors"
          >
            {mine ? "Count this device" : "Don't count this device"}
          </button>
        </div>

        {error && <div className="bg-red-50 text-red-700 rounded-md p-3">{error}</div>}
        {!data && !error && <div className="text-[#795548]">Loading…</div>}

        {data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-cards">
              <Card icon={Eye} label="Visits" value={t.visits} note="times the page was opened" />
              <Card icon={Users} label="Visitors" value={t.visitors} note="different people (counted per day)" />
              <Card icon={Search} label="Lookups" value={t.lookups} note={`${t.not_found} number(s) not found`} />
              <Card icon={FileCheck} label="Labels seen" value={t.labels_seen} note={`${t.unlocked} with last name`} />
            </div>

            <div className="bg-white rounded-xl border border-[#D7CCC8] p-5">
              <h2 className="text-lg font-bold text-[#5D4037] mb-3">Day by day</h2>
              {data.per_day.length === 0 ? (
                <p className="text-[#795548]">No visits yet in this period.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[#795548]"><th className="py-1">Day</th><th>Visits</th><th>Visitors</th><th>Lookups</th><th>Labels seen</th><th className="w-1/3"></th></tr></thead>
                  <tbody>
                    {[...data.per_day].reverse().map((d) => (
                      <tr key={d.day} className="border-t border-[#EFEBE9] text-[#5D4037]">
                        <td className="py-1.5">{d.day}</td><td>{d.visits}</td><td>{d.visitors}</td><td>{d.lookups}</td><td>{d.labels_seen}</td>
                        <td><div className="h-2 rounded bg-[#5D4037]" style={{ width: `${((d.visits + d.lookups) / maxDay) * 100}%` }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-[#D7CCC8] p-5">
                <h2 className="text-lg font-bold text-[#5D4037] mb-3">Labels viewed, by blend</h2>
                {data.top_products.length === 0 ? <p className="text-[#795548]">None yet.</p> : (
                  <table className="w-full text-sm"><tbody>
                    {data.top_products.map((p) => (
                      <tr key={p.product} className="border-t border-[#EFEBE9] text-[#5D4037]"><td className="py-1.5">{p.product}</td><td className="text-right font-bold">{p.views}</td></tr>
                    ))}
                  </tbody></table>
                )}
              </div>
              <div className="bg-white rounded-xl border border-[#D7CCC8] p-5">
                <h2 className="text-lg font-bold text-[#5D4037] mb-3">Latest lookups</h2>
                {data.recent_lookups.length === 0 ? <p className="text-[#795548]">None yet.</p> : (
                  <table className="w-full text-sm"><tbody>
                    {data.recent_lookups.map((r, i) => (
                      <tr key={i} className="border-t border-[#EFEBE9] text-[#5D4037]">
                        <td className="py-1.5 whitespace-nowrap">{new Date(r.at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                        <td className="font-medium">{r.order_number || "—"}</td>
                        <td>{RESULT_TEXT[r.result] || r.result}{r.products?.length ? `: ${r.products.join(", ")}` : ""}</td>
                      </tr>
                    ))}
                  </tbody></table>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default VisitorStats;
